import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, X, ChevronDown, ChevronUp, Phone } from "lucide-react";
import Cookies from "js-cookie";
import apiConfig from "../config/apiConfig";
import HelpIcon from "../images/help_icon.png";
import { logout } from "../apis/backend";
import SidebarAdmin from "./Utils/SidebarAdmin";
import "./Applications.css";
import { toast } from "../utils/toast";
import LogoutButton from "./LogoutButton";
import RenderImage from "./QueryManager/RenderImage/RenderImage";
import { apiFetch } from "../apis/apiFetch";
import * as XLSX from "xlsx";
import { useAdmissionDriveKind } from "../hooks/useAdmissionDriveKind";
import { AdmissionDriveKind } from "../utils/applicationFlow";
import { useExamdetailsViewModel } from "../viewModels/useExamDetailsViewMore";
import { useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import { queryService } from "../services/QueryService";
import { LoginContext } from "../context/LoginContext";
import { messageService } from "../services/MessageService";
import { help_desk_query } from "./QueryManager/QueryChat/QueryChatPopup";
import moment from "moment";

interface Query {
  id: string;
  queryId: string;
  user_email: string;
  subject: string;
  category: string;
  status: "Open" | "Resolved";
  last_updated_at: string;
  user_name: string;
  drive_id: string;
  user_id: string;
}

export interface ApplicationRow {
  id: string;
  application_number: string;
  user_id: string;
  drive_id: string;
  status: string;
  extra_data?: {
    user_name?: string;
    user_email?: string;
    user_mobile?: string;
    payment_status?: string;
    drive_name?: string;
    total_count?: number;
    page_no?: number;
    page_size?: number;
    [key: string]: any;
  };
  [key: string]: any;
}

interface DriveOption {
  id: string;
  name: string;
  type: string;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "#6b7280",
  SUBMITTED: "#1d4ed8",
  UNDER_REVIEW: "#d97706",
  ACCEPTED: "#059669",
  REJECTED: "#dc2626",
  REJECTED_BY_ADMIN: "#7f1d1d",
  EDITING: "#6b7280",
  EDIT_SUBMIT: "#06b6d4",
};

const PAYMENT_COLORS: Record<string, string> = {
  SUCCESS: "#059669",
  PENDING: "#d97706",
  FAILED: "#dc2626",
  NOT_PAID: "#6b7280",
};

const SECTION_LABELS: Record<string, string> = {
  personal_details: "Personal Details",
  current_address: "Current Address",
  permanent_address: "Permanent Address",
  guardian_details: "Parent/Guardian Details",
  // education_10th: "Education — 10th",
  // education_12th: "Education — 12th",
  graduation_details: "Graduation Details",
  education_details: "Education Details",
  exam_details: "Exam Details",
  programme_preference: "Programme",
  gate_details: "GATE Details",
  payment: "Payment Details",
};

const FIELD_LABELS: Record<string, string> = {
  // Personal Details (Applicant)
  fullname: "Full Name",
  email: "Email",
  mobile: "Mobile",
  dob: "Date of Birth",
  gender: "Gender",
  photo: "Photo",
  nationality: "Nationality",
  // Address
  line1: "Address Line 1",
  line2: "Address Line 2",
  city: "City",
  state: "State",
  pincode: "Pincode",
  country: "Country",
  // Guardian
  name: "Name",
  relation: "Relation",
  alternatemobile: "Alternate Mobile",
  // Graduation
  collegename: "College Name",
  degree: "Degree",
  major: "Branch",
  cgpa: "Percentage",
  passingyear: "Year of Passing",
  marks_obtained: "Marks/ CGPA Obtained",
  maximum_marks: "Maximum Marks/CGPA",
  // Programme Preference
  pref1: "Programme 1",
  pref2: "Programme 2",
  pref3: "Programme 3",
  pref1scorecard: "Programme 1 Scorecard",
  pref2scorecard: "Programme 2 Scorecard",
  pref3scorecard: "Programme 3 Scorecard",
  pref1rollno: "Programme 1 Roll No.",
  pref2rollno: "Programme 2 Roll No.",
  pref3rollno: "Programme 3 Roll No.",
  pref1score: "Programme 1 Score",
  pref2score: "Programme 2 Score",
  pref3score: "Programme 3 Score",
  pref1rank: "Programme 1 Rank",
  pref2rank: "Programme 2 Rank",
  pref3rank: "Programme 3 Rank",
  pref1subjects: "Programme 1 Subject",
  pref2subjects: "Programme 2 Subject",
  pref3subjects: "Programme 3 Subject",
  pref1year: "Programme 1 Year",
  pref2year: "Programme 2 Year",
  pref3year: "Programme 3 Year",
  // GATE
  subjects: "Subject",
  score: "GATE Score",
  rank: "GATE Rank",
  rollno: "GATE Roll No.",
  scorecard: "Scorecard",
  // Payment
  transaction_id: "Transaction ID",
  amount: "Amount",
  status: "Status",
  payment_date: "Payment Date",
  payment_mode: "Payment Mode",
  reference_no: "Reference No.",
  user_id: "User ID",
  // BTech Specific Labels
  edu10_board: "Education Board",
  edu10_schoolname: "School/College",
  edu10_passingyear: "Year of Passing",
  edu10_marksformat: "Grade/Marks Type",
  edu10_marksobtained: "Marks Obtained",
  edu10_maximummarks: "Maximum Marks",
  edu10_percentage: "Percentage",
  edu10_grade: "Grade",
  edu12_board: "Education Board",
  edu12_schoolname: "School/College",
  edu12_passingyear: "Year of Passing",
  edu12_specialization: "Specialization",
  edu12_marksformat: "Grade/Marks Type",
  edu12_marksobtained: "Marks Obtained",
  edu12_maximummarks: "Maximum Marks",
  edu12_percentage: "Percentage",
  edu12_grade: "Grade",
  edu12_percentagemaths: "Mathematics Percentage",
  pref4: "Programme 4",
  pref5: "Programme 5",
  jeemain_rollno: "JEE Main Application No.",
  jeemain_score: "JEE Main NTA Final Total Score",
  jeemain_rank: "JEE Main Rank (CRL)",
  jeemain_scorecard: "JEE Main Scorecard",
  jeeadv_rollno: "JEE Advanced Roll No.",
  jeeadv_score: "JEE Advanced Score",
  jeeadv_rank: "JEE Advanced Rank (CRL)",
  jeeadv_scorecard: "JEE Advanced Scorecard",
  olympiad_name: "Olympiad Name",
  olympiad_no: "Olympiad Roll No.",
  olympiad_proof: "Olympiad Proof",
  olympiad_year: "Olympiad Year",
  // Internal Subheaders
  _edu10_header: "Class Xth / SSC",
  _edu12_header: "Class XIIth / HSC",
  _jeemain_header: "JEE Main",
  _jeeadv_header: "JEE Advanced (Optional)",
  _olympiad_header: "Olympiad Details (Optional)",
};

// Fields to skip in detail view
const SKIP_FIELDS = new Set([
  "id",
  "g_created_by_id",
  "g_created_by_name",
  "g_modified_by_id",
  "g_modified_by_name",
  "g_creation_time",
  "g_modify_time",
  "g_soft_delete",
  "g_status",
  "archived",
  "archived_time",
  "extra_data",
  "grade",
  "edu10_grade",
  "edu12_grade",
]);

// Define exact field display order for each section (using actual DB field names)
const SECTION_FIELD_ORDER: Record<string, string[]> = {
  personal_details: [
    "fullname",
    "email",
    "mobile",
    "dob",
    "gender",
    "nationality",
    "abc_id",
    "photo",
  ],
  current_address: ["line1", "line2", "city", "state", "pincode", "country"],
  permanent_address: ["line1", "line2", "city", "state", "pincode", "country"],
  guardian_details: ["name", "relation", "mobile", "email", "alternatemobile"],
  graduation_details: [
    "collegename",
    "degree",
    "major",
    "passingyear",
    "marks_obtained",
    "maximum_marks",
    "cgpa",
  ],
  education_details: [
    "_edu10_header",
    "edu10_board",
    "edu10_schoolname",
    "edu10_passingyear",
    "edu10_marksformat",
    "edu10_marksobtained",
    "edu10_maximummarks",
    "edu10_percentage",
    "_edu12_header",
    "edu12_board",
    "edu12_schoolname",
    "edu12_passingyear",
    "edu12_percentagemaths",
    "edu12_marksformat",
    "edu12_marksobtained",
    "edu12_maximummarks",
    "edu12_percentage",
  ],
  exam_details: [
    "_jeemain_header",
    "jeemain_rollno",
    "jeemain_score",
    "jeemain_rank",
    "jeemain_scorecard",
    "_jeeadv_header",
    "jeeadv_rollno",
    "jeeadv_score",
    "jeeadv_rank",
    "jeeadv_scorecard",
    "_olympiad_header",
    "olympiad_name",
    "olympiad_no",
    "olympiad_proof",
    "olympiad_year",
  ],
  programme_preference: [
    "pref1",
    "pref1rollno",
    "pref1score",
    "pref1rank",
    "pref1subjects",
    "pref1year",
    "pref1scorecard",
    "pref2",
    "pref2rollno",
    "pref2score",
    "pref2rank",
    "pref2subjects",
    "pref2year",
    "pref2scorecard",
    "pref3",
    "pref3rollno",
    "pref3score",
    "pref3rank",
    "pref3subjects",
    "pref3year",
    "pref3scorecard",
    "pref4",
    "pref5",
  ],
  gate_details: ["subjects", "score", "rank", "rollno", "year", "scorecard"],
  payment: [
    "transaction_id",
    "amount",
    "status",
    "payment_date",
    "payment_mode",
    "reference_no",
  ],
};

export default function Applications() {
  const navigate = useNavigate();
  const { kind: globalKind } = useAdmissionDriveKind();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [allApplications, setAllApplications] = useState<ApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupData, setPopupData] = useState<Record<string, any> | null>(null);
  const [popupAppNumber, setPopupAppNumber] = useState("");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Filter state
  const [filterFormStatus, setFilterFormStatus] = useState("");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterVerificationStatus, setFilterVerificaitonStatus] = useState("");
  const [filterJeeMainVerificationStatus, setFilterJeeMainVerificationStatus] = useState("");
  const [filterJeeAdvVerificationStatus, setFilterJeeAdvVerificationStatus] = useState("");
  const [filterDriveId, setFilterDriveId] = useState("");
  const [filterPref, setFilterPref] = useState("");
  const [searchText, setSearchText] = useState("");
  const [drives, setDrives] = useState<DriveOption[]>([]);

  // Rejection state
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionProgramme, setRejectionProgramme] = useState("");
  const [rejectionLoading, setRejectionLoading] = useState(false);

  // Pagination state
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showAdv, setShowAdv] = useState(false)
  const [showJeeEdit, setShowJeeEdit] = useState(false)
  const [showJeeMainEdit, setShowJeeMainEdit] = useState(false)
  const [showJeeAdvEdit, setShowJeeAdvEdit] = useState(false)
  const [appVerified, setAppVerified] = useState(false)
  const [quillMsg, setQuillMsg] = useState("")
  const [query, setQuery] = useState<help_desk_query[] | null>(null)


  useEffect(() => {
    fetchDrives();
    fetchApplications();
  }, []);

  useEffect(() => {
    setPageNo(1);
    fetchApplications();
  }, [
    filterFormStatus,
    filterPaymentStatus,
    filterVerificationStatus,
    filterJeeMainVerificationStatus,
    filterJeeAdvVerificationStatus,
    filterDriveId,
    searchText,
    pageSize,
    filterPref,
  ]);

  const fetchDrives = async () => {
    try {
      const res = await apiFetch(
        `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`,
        { method: "GET" },
      );
      if (res.ok) {
        const data = await res.json();
        setDrives(
          (data.resource || []).map((d: any) => ({
            id: d.id,
            name: d.name,
            type: d.drivetype,
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching drives:", err);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      // Fetch everything at once for frontend filtering/pagination
      const argParts: string[] = ["page_no:1", "page_size:100000"];
      if (filterFormStatus) argParts.push(`form_status:${filterFormStatus}`);
      if (filterPaymentStatus)
        argParts.push(`payment_status:${filterPaymentStatus}`);
      if (filterVerificationStatus)
        argParts.push(`verification_status:${filterVerificationStatus}`);
      if (filterDriveId) argParts.push(`drive_id:${filterDriveId}`);
      if (filterPref) argParts.push(`pref_filter:${filterPref}`);
      const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_ALL_APPLICATIONS&args=${encodeURIComponent(argParts.join(","))}`;

      const res = await apiFetch(url, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        const resources: ApplicationRow[] = data.resource || [];

        // Filter out carrier-only records
        const validApps = resources.filter(
          (r) => r.application_number && r.application_number !== "PENDING",
        );
        setAllApplications(validApps);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = allApplications.filter((app) => {
    if (filterFormStatus && app.status !== filterFormStatus) return false;

    const extra = app.extra_data || {};
    const payStatus = extra.payment_status || "NOT_PAID";
    if (filterPaymentStatus && payStatus !== filterPaymentStatus) return false;

    if (filterVerificationStatus && app?.verification_status?.toString() !== filterVerificationStatus) return false;

    if (filterJeeMainVerificationStatus) {
      const jeeMainStatus = app.jee_main_verification_status || "PENDING";
      if (jeeMainStatus !== filterJeeMainVerificationStatus) return false;
    }

    if (filterJeeAdvVerificationStatus) {
      const jeeAdvStatus = app.jee_advanced_verification_status || "PENDING";
      if (jeeAdvStatus !== filterJeeAdvVerificationStatus) return false;
    }

    if (filterDriveId && app.drive_id !== filterDriveId) return false;

    if (searchText) {
      const term = searchText.toLowerCase();
      const numMatch = (app.application_number || "")
        .toLowerCase()
        .includes(term);
      const nameMatch = (extra.user_name || "").toLowerCase().includes(term);
      const emailMatch = (extra.user_email || "").toLowerCase().includes(term);
      if (!numMatch && !nameMatch && !emailMatch) return false;
    }

    return true;
  });

  const totalCount = filteredApplications.length;
  const startIndex = (pageNo - 1) * pageSize;
  const applications = filteredApplications.slice(
    startIndex,
    startIndex + pageSize,
  );

  const mapFlatToSections = (record: any): Record<string, any> => {
    const sectioned: Record<string, any> = {
      personal_details: {
        fullname: record.fullname,
        email: record.email,
        mobile: record.mobile,
        dob: record.dob,
        gender: record.gender,
        nationality: record.nationality,
        photo: record.photo,
        abc_id: record.abc_id,
      },
      current_address: {
        line1: record.line1,
        line2: record.line2,
        city: record.city,
        state: record.state,
        pincode: record.pincode,
        country: record.country,
      },
      permanent_address: {
        line1: record.p_line1,
        line2: record.p_line2,
        city: record.p_city,
        state: record.p_state,
        pincode: record.p_pincode,
        country: record.p_country,
      },
      guardian_details: {
        name: record.guardian_name,
        relation: record.guardian_relation,
        mobile: record.guardian_mobile,
        email: record.guardian_email,
        alternatemobile: record.alternatemobile,
      },
      graduation_details: {
        collegename: record.collegename,
        degree: record.degree,
        major: record.major,
        passingyear: record.passingyear,
        marks_obtained: record.marks_obtained,
        maximum_marks: record.maximum_marks,
        cgpa: record.cgpa,
      },
      education_details: {
        edu10_board: record.edu10_board,
        edu10_schoolname: record.edu10_schoolname,
        edu10_passingyear: record.edu10_passingyear,
        edu10_marksformat: record.edu10_marksformat,
        edu10_marksobtained: record.edu10_marksobtained,
        edu10_maximummarks: record.edu10_maximummarks,
        edu10_percentage: record.edu10_percentage,
        edu10_grade: record.edu10_grade,
        edu12_board: record.edu12_board,
        edu12_schoolname: record.edu12_schoolname,
        edu12_passingyear: record.edu12_passingyear,
        edu12_percentagemaths: record.edu12_percentagemaths,
        edu12_marksformat: record.edu12_marksformat,
        edu12_marksobtained: record.edu12_marksobtained,
        edu12_maximummarks: record.edu12_maximummarks,
        edu12_percentage: record.edu12_percentage,
        edu12_since: record.edu12_since,
        edu12_grade: record.edu12_grade,
      },
      exam_details: {
        jeemain_rollno: record.jeemain_rollno,
        jeemain_score: record.jeemain_score,
        jeemain_rank: record.jeemain_rank,
        jeemain_scorecard: record.jeemain_scorecard,
        jeeadv_rollno: record.jeeadv_rollno,
        jeeadv_score: record.jeeadv_score,
        jeeadv_rank: record.jeeadv_rank,
        jeeadv_scorecard: record.jeeadv_scorecard,
        olympiad_name: record.olympiad_name,
        olympiad_no: record.olympiad_no,
        olympiad_proof: record.olympiad_proof,
        olympiad_year: record.olympiad_year,
      },
      programme_preference: {
        pref1: record.pref1,
        pref1rollno: record.pref1rollno,
        pref1score: record.pref1score,
        pref1rank: record.pref1rank,
        pref1subjects: record.pref1subjects,
        pref1year: record.pref1year,
        pref2: record.pref2,
        pref2rollno: record.pref2rollno,
        pref2score: record.pref2score,
        pref2rank: record.pref2rank,
        pref2subjects: record.pref2subjects,
        pref2year: record.pref2year,
        pref3: record.pref3,
        pref1scorecard: record.pref1scorecard,
        pref2scorecard: record.pref2scorecard,
        pref3scorecard: record.pref3scorecard,
        pref3rollno: record.pref3rollno,
        pref3score: record.pref3score,
        pref3rank: record.pref3rank,
        pref3subjects: record.pref3subjects,
        pref3year: record.pref3year,
        pref4: record.pref4,
        pref5: record.pref5,
      },
      gate_details: {
        subjects:
          record.pref1subjects || record.pref2subjects || record.pref3subjects,
        score: record.pref1score || record.pref2score || record.pref3score,
        rank: record.pref1rank || record.pref2rank || record.pref3rank,
        rollno: record.pref1rollno || record.pref2rollno || record.pref3rollno,
        year: record.pref1year || record.pref2year || record.pref3year,
        scorecard:
          record.pref1scorecard ||
          record.pref2scorecard ||
          record.pref3scorecard,
      },
      payment: {
        transaction_id: record.transaction_id,
        amount: record.amount,
        status: record.payment_status,
        payment_date: record.payment_date,
        payment_mode: record.payment_gateway,
        reference_no: record.reference_no,
      },
    };

    // Remove sections where all values are null/undefined/empty
    Object.keys(sectioned).forEach((key) => {
      const section = sectioned[key];
      const hasData = Object.values(section).some(
        (v) => v !== null && v !== undefined && v !== "",
      );
      if (!hasData) delete sectioned[key];
    });

    (sectioned as any).drive_id = record.drive_id;
    (sectioned as any).application_number = record.application_number;
    (sectioned as any).admin_rejection_reason = record.admin_rejection_reason;
    (sectioned as any).admin_rejected_programmes = record.admin_rejected_programmes;
    (sectioned as any).id = record.id;

    return sectioned;
  };

  const userId = "student"; // replace if from context
  const appId = "admission-portal";

  const {
    dataToSave,
    setDataToSave,
    save,
  } = useExamdetailsViewModel(userId, appId);

  // const [appEditing, setAppEditing] = useState("")
  // const [isAppVerfied, setIsAppVerified] = useState("")
  // const [appPayment, setAppPayment] = useState("")
  const selectedApplication = allApplications.filter((app) => app.application_number === popupData?.application_number)
  const isApplicationEditing = selectedApplication?.[0]?.status === "EDITING"
  const isJeeEditing = selectedApplication?.[0]?.jee_main_verification_status === "JEE_EDITING" && selectedApplication?.[0]?.jee_advanced_verification_status === "JEE_EDITING"
  const isJeeMainEditing = selectedApplication?.[0]?.jee_main_verification_status === "JEE_EDITING"
  const isJeeAdvEditing = selectedApplication?.[0]?.jee_advanced_verification_status === "JEE_EDITING"
  const isApplicationVerfied = selectedApplication?.[0]?.verification_status === true
  useEffect(() => {
    if (popupData) {
      // application_number
      const app = allApplications.filter((app) => app.application_number === popupData.application_number)
      const { extra_data, ...data } = app?.[0]
      console.log("popupData", data)
      setDataToSave((prev: any) => ({
        ...data,   // ✅ DB data
        ...prev,         // ✅ preserve any edits
      }));
      if (data?.id) {
        fetchQueryDetails(data?.id)
      } else {
        setQuery(null)
      }
    }
    (isApplicationEditing || isJeeMainEditing || isJeeAdvEditing) ? setShowAdv(true) : setShowAdv(false)
    isApplicationVerfied ? setAppVerified(true) : setAppVerified(false)
    isJeeEditing ? setShowJeeEdit(true) : setShowJeeEdit(false)
    isJeeMainEditing ? setShowJeeMainEdit(true) : setShowJeeMainEdit(false)
    isJeeAdvEditing ? setShowJeeAdvEdit(true) : setShowJeeAdvEdit(false)
  }, [popupData, dataToSave?.user_id, isApplicationEditing, isApplicationVerfied, isJeeEditing, isJeeMainEditing, isJeeAdvEditing]);



  const fetchApplicationDetails = (userId: string, appNumber: string) => {
    setDataToSave(null)
    setPopupAppNumber(appNumber);
    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);
    setExpandedSections(new Set(Object.keys(SECTION_LABELS)));

    // Use data already loaded in applications state — no separate API call needed
    const app = applications.find(
      (a) => a.user_id === userId && a.application_number === appNumber,
    );
    if (app) {
      setPopupData(mapFlatToSections(app));
    } else {
      setPopupData({});
    }
    setPopupLoading(false);
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleRowSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const selectableApps = applications.filter((app) => app.status !== "DRAFT");

    if (selectedIds.length === selectableApps.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableApps.map((app) => app.id));
    }
  };

  const updateStatus = async (status: "ACCEPTED" | "REJECTED") => {
    if (selectedIds.length === 0) return;

    // login token check removed - apiFetch handles refresh

    try {
      for (const id of selectedIds) {
        const resourcePayload = {
          id: id,
          status: status,
        };

        const formData = new URLSearchParams();
        const jsonString = JSON.stringify(resourcePayload);
        const base64String = btoa(jsonString);

        formData.append("resource", base64String);
        formData.append("action", "UPDATE_STATUS");

        const res = await apiFetch(apiConfig.getResourceUrl("applications"), {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        });

        if (!res.ok) {
          throw new Error("Failed updating id: " + id);
        }
      }

      toast.success(
        `${selectedIds.length} application(s) ${status.toLowerCase()} successfully`,
      );
      setSelectedIds([]);
      fetchApplications();
    } catch (err) {
      console.error(err);
      toast.error("Bulk update failed");
    }
  };

  const closeRejectionModal = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
    setRejectionProgramme("");
  };

  const handleRejectApplication = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    if (!rejectionProgramme) {
      toast.error("Please select a programme to reject");
      return;
    }

    setRejectionLoading(true);
    try {
      const payload = {
        application_id: (popupData as any)?.id,
        reason: rejectionReason,
        programme: rejectionProgramme
      };

      const argString = Object.entries(payload)
        .map(([k, v]) => `${k}:${v}`)
        .join(",");

      const url = `${apiConfig.getResourceUrl("applications")}?queryId=REJECT_APPLICATION&args=${encodeURIComponent(argString)}`;

      const res = await apiFetch(url, { method: "GET" });

      if (res.ok) {
        toast.success("Application rejected successfully");
        closeRejectionModal();
        setShowPopup(false);
        fetchApplications();
      } else {
        throw new Error("Failed to reject application");
      }
    } catch (err) {
      console.error(err);
      toast.error("Rejection failed");
    } finally {
      setRejectionLoading(false);
    }
  };

  const getEligibleProgrammes = () => {
    if (!popupData || !popupData.programme_preference) return [];
    const prefs = popupData.programme_preference;
    const progs: string[] = [];
    if (prefs.pref1 && prefs.pref1 !== "—") progs.push(prefs.pref1);
    if (prefs.pref2 && prefs.pref2 !== "—") progs.push(prefs.pref2);
    if (prefs.pref3 && prefs.pref3 !== "—") progs.push(prefs.pref3);
    if (prefs.pref4 && prefs.pref4 !== "—") progs.push(prefs.pref4);
    if (prefs.pref5 && prefs.pref5 !== "—") progs.push(prefs.pref5);
    return [...new Set(progs)];
  };

  console.log("popupDatapopupDatapopupData", dataToSave)
  const exportToCSV = async () => {
    setExporting(true);
    try {
      const validApps = filteredApplications;

      if (validApps.length === 0) {
        toast.error("No valid applications to export");
        setExporting(false);
        return;
      }

      const sections = Object.keys(SECTION_LABELS);
      const dynamicAccessors: { section: string; key: string }[] = [];

      const mappedApps = validApps.map((row) => ({
        row,
        sections: mapFlatToSections(row),
      }));

      const sectionKeys = new Map<string, Set<string>>();
      sections.forEach((s) =>
        sectionKeys.set(s, new Set(SECTION_FIELD_ORDER[s] || [])),
      );

      mappedApps.forEach(({ sections: mapped }) => {
        sections.forEach((sec) => {
          const secData = mapped[sec];
          if (secData) {
            Object.keys(secData).forEach((k) => {
              if (!SKIP_FIELDS.has(k)) {
                sectionKeys.get(sec)!.add(k);
              }
            });
          }
        });
      });

      const headerRow: string[] = [
        "Application No.",
        "User Name",
        "Email",
        "Mobile",
        "Drive Name",
        "Application Status",
        "Payment Status",
        "JEE Main Verification Status",
        "JEE Advanced Verification Status",
      ];

      const currentDrive = filterDriveId ? drives.find((d) => d.id === filterDriveId) : null;
      const isBtechExport = currentDrive?.type?.toLowerCase() === "btech";

      sections.forEach((sec) => {
        if (isBtechExport) {
          if (sec === "gate_details" || sec === "graduation_details") return;
        } else {
          if (sec === "programme_preference") return;
        }

        const keys = Array.from(sectionKeys.get(sec)!);
        keys.forEach((k) => {
          // For BTech, exclude all the extra score/rank fields from preferences, just keep the prog names
          if (isBtechExport && sec === "programme_preference") {
            if (!/^pref[1-5]$/.test(k)) return;
          }
          headerRow.push(`${SECTION_LABELS[sec]} - ${formatFieldLabel(k)}`);
          dynamicAccessors.push({ section: sec, key: k });
        });
      });

      const sheetsData: Record<string, any[][]> = {};

      mappedApps.forEach(({ row, sections: mapped }) => {
        const extra = row.extra_data || {};

        const baseExcelRow = [
          row.application_number,
          extra.user_name,
          extra.user_email,
          extra.user_mobile,
          extra.drive_name,
          row.status?.replace(/_/g, " ") || row.status,
          (extra.payment_status || "NOT PAID")?.replace(/_/g, " "),
          row.jee_main_verification_status || "PENDING",
          row.jee_advanced_verification_status || "PENDING",
        ];

        if (isBtechExport) {
          const sheetName = "BTech Applications";
          if (!sheetsData[sheetName]) sheetsData[sheetName] = [headerRow];

          const dynamicRow = dynamicAccessors.map(({ section, key }) => {
            let val = (mapped[section] || {})[key];

            if (section === "personal_details" && !val) {
              if (key === "fullname") val = extra.user_name;
              if (key === "email") val = extra.user_email;
              if (key === "mobile") val = extra.user_mobile;
            }

            if (isBtechExport && key.startsWith("_") && key.endsWith("_header")) {
              val = FIELD_LABELS[key] || "";
            }

            if (key === "photo" && typeof val === "object") {
              val = val?.url || val?.path || val;
            } else if (key === "scorecard" && typeof val === "object") {
              val = val?.url || val?.path || val;
            }

            return formatFieldValue(key, val, true);
          });

          sheetsData[sheetName].push([...baseExcelRow, ...dynamicRow]);
          return;
        }

        const progPref = mapped["programme_preference"] || {};
        const prefs: string[] = [];

        if (progPref.pref1 && progPref.pref1 !== "—")
          prefs.push(String(progPref.pref1));
        if (progPref.pref2 && progPref.pref2 !== "—")
          prefs.push(String(progPref.pref2));
        if (progPref.pref3 && progPref.pref3 !== "—")
          prefs.push(String(progPref.pref3));

        if (prefs.length === 0) {
          const sheetName = "No Preference";
          if (!sheetsData[sheetName]) sheetsData[sheetName] = [headerRow];

          const dynamicRow = dynamicAccessors.map(({ section, key }) => {
            let val = (mapped[section] || {})[key];

            if (section === "personal_details" && !val) {
              if (key === "fullname") val = extra.user_name;
              if (key === "email") val = extra.user_email;
              if (key === "mobile") val = extra.user_mobile;
            }

            if (key === "photo" && typeof val === "object") {
              val = val?.url || val?.path || val;
            } else if (key === "scorecard" && typeof val === "object") {
              val = val?.url || val?.path || val;
            }

            return formatFieldValue(key, val, true);
          });

          sheetsData[sheetName].push([...baseExcelRow, ...dynamicRow]);
        } else {
          prefs.forEach((currentPref) => {
            if (filterPref && currentPref !== filterPref) return;

            let sheetName = currentPref
              .substring(0, 31)
              .replace(/[\\/*?:[\]]/g, "");

            if (!sheetName) sheetName = "Unknown";
            if (!sheetsData[sheetName]) sheetsData[sheetName] = [headerRow];

            const dynamicRow = dynamicAccessors.map(({ section, key }) => {
              let val = (mapped[section] || {})[key];

              // ✅ FIX 1: Filter preference fields
              if (section === "programme_preference") {
                if (key.startsWith("pref1") && progPref.pref1 !== currentPref)
                  val = "";
                if (key.startsWith("pref2") && progPref.pref2 !== currentPref)
                  val = "";
                if (key.startsWith("pref3") && progPref.pref3 !== currentPref)
                  val = "";
              }

              // ✅ FIX 2: Correct GATE mapping per sheet
              if (section === "gate_details") {
                if (progPref.pref1 === currentPref) {
                  val = {
                    subjects: progPref.pref1subjects,
                    score: progPref.pref1score,
                    rank: progPref.pref1rank,
                    rollno: progPref.pref1rollno,
                    year: progPref.pref1year,
                    scorecard: progPref.pref1scorecard,
                  }[key];
                } else if (progPref.pref2 === currentPref) {
                  val = {
                    subjects: progPref.pref2subjects,
                    score: progPref.pref2score,
                    rank: progPref.pref2rank,
                    rollno: progPref.pref2rollno,
                    year: progPref.pref2year,
                    scorecard: progPref.pref2scorecard,
                  }[key];
                } else if (progPref.pref3 === currentPref) {
                  val = {
                    subjects: progPref.pref3subjects,
                    score: progPref.pref3score,
                    rank: progPref.pref3rank,
                    rollno: progPref.pref3rollno,
                    year: progPref.pref3year,
                    scorecard: progPref.pref3scorecard,
                  }[key];
                }
              }

              if (section === "personal_details" && !val) {
                if (key === "fullname") val = extra.user_name;
                if (key === "email") val = extra.user_email;
                if (key === "mobile") val = extra.user_mobile;
              }

              if (key === "photo" && typeof val === "object") {
                val = val?.url || val?.path || val;
              } else if (key === "scorecard" && typeof val === "object") {
                val = val?.url || val?.path || val;
              }

              return formatFieldValue(key, val, true);
            });

            sheetsData[sheetName].push([...baseExcelRow, ...dynamicRow]);
          });
        }
      });

      if (Object.keys(sheetsData).length === 0) {
        toast.error("No valid data to export");
        return;
      }

      const wb = XLSX.utils.book_new();
      Object.keys(sheetsData).forEach((sheetName) => {
        const ws = XLSX.utils.aoa_to_sheet(sheetsData[sheetName]);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(
        wb,
        `Applications_Export_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };
  const formatFieldLabel = (key: string) => {
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatEnumStatus = (status: string | undefined) => {
    if (!status) return "Pending";
    if (status === "PENDING") return "Pending";
    if (status === "ACCEPTED") return "Accepted";
    if (status === "NOT_ACCEPTED") return "Not Accepted";
    if (status === "JEE_EDITING") return "JEE Editing";
    if (status === "JEE_MAIN_RESUBMITTED") return "JEE Main Resubmitted";
    if (status === "JEE_ADVANCED_RESUBMITTED") return "JEE Advanced Resubmitted";
    return status;
  };

  const formatFieldValue = (
    key: string,
    val: any,
    isExport: boolean = false,
  ): React.ReactNode => {
    if (val === null || val === undefined || val === "")
      return isExport ? "" : "—";
    // Strip time from date fields (e.g. dob)
    if (key === "dob") {
      try {
        const d = new Date(typeof val === "number" ? val : String(val));
        if (!isNaN(d.getTime())) {
          // Format as DD-MM-YYYY
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${dd}-${mm}-${yyyy}`;
        }
      } catch {
        /* fall through */
      }
    }
    if (
      key === "photo" ||
      key === "scorecard" ||
      key === "pref1scorecard" ||
      key === "pref2scorecard" ||
      key === "pref3scorecard" ||
      key === "jeemain_scorecard" ||
      key === "jeeadv_scorecard" ||
      key === "olympiad_proof"
    ) {
      if (isExport) {
        if (!val) return "NA";
        if (typeof val === "string") return val;
        if (typeof val === "object")
          return val.url || val.path || val.fileUrl || JSON.stringify(val);
        return String(val);
      }
      if (val) {
        return <RenderImage photo={val} key={key} />;
      } else {
        return "NA";
      }
    }
    if (
      typeof val === "object" &&
      val instanceof Object &&
      !(val instanceof Array)
    ) {
      // If it's a Date-like object with a string representation
      const str = String(val);
      if (str.includes("T")) return str.split("T")[0];
      return JSON.stringify(val);
    }
    return String(val);
  };

  const renderCircleStatus = (status: string | boolean | undefined) => {
    let type: "success" | "error" | "pending" | "editing" | "resubmitted" = "pending";
    let title = "Pending";

    if (status === true || status === "ACCEPTED") {
      type = "success";
      title = "Accepted";
    } else if (status === false || status === "NOT_ACCEPTED") {
      type = "error";
      title = "Not Accepted";
    } else if (status === "JEE_EDITING") {
      type = "editing";
      title = "JEE Editing";
    } else if (status === "JEE_MAIN_RESUBMITTED") {
      type = "resubmitted";
      title = "JEE Main Resubmitted";
    } else if (status === "JEE_ADVANCED_RESUBMITTED") {
      type = "resubmitted";
      title = "JEE Advanced Resubmitted";
    }

    const colors = {
      success: { bg: "#e6f4ea", text: "#137333", border: "#ceead6", char: "✓" },
      error: { bg: "#fce8e6", text: "#c5221f", border: "#fad2cf", char: "✗" },
      pending: { bg: "#f1f3f4", text: "#5f6368", border: "#dadce0", char: "—" },
      editing: { bg: "#fef3c7", text: "#b45309", border: "#fde68a", char: "JEE_EDITING" },
      resubmitted: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", char: "RESUBMITTED" },
    };

    const config = colors[type];

    if (type === "editing" || type === "resubmitted") {
      return (
        <span
          title={title}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: config.bg,
            color: config.text,
            border: `1px solid ${config.border}`,
            borderRadius: "4px",
            padding: "2px 6px",
            fontSize: "11px",
            fontWeight: "bold",
            lineHeight: 1,
          }}
        >
          {config.char}
        </span>
      );
    }

    return (
      <span
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "22px",
          height: "22px",
          backgroundColor: config.bg,
          color: config.text,
          border: `1px solid ${config.border}`,
          borderRadius: "50%",
          fontSize: "12px",
          fontWeight: "bold",
          lineHeight: 1,
        }}
      >
        {config.char}
      </span>
    );
  };

  const { user } = useContext(LoginContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); // Start loading indicator

    try {
      // First allow the edit status change
      // await handleAllowEdit(true); // Allow editing

      // Then submit the message
      await handleMsgSubmit(e); // Submit the message

    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("Error processing your request. Please try again.");
    } finally {
      setLoading(false); // Stop loading indicator
    }
  };

  const handleAllowEdit = async (checked: boolean) => {
    try {
      const { extra_data, ...data } = dataToSave;
      const newStatus = checked ? "EDITING" : "SUBMITTED";
      const finalPayload = { status: newStatus };
      await save(finalPayload);

      // Optimized: patch just this record instead of re-fetching all
      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === dataToSave.id ? { ...app, status: newStatus } : app
        )
      );

      if (checked) toast.success("Application Edit allowed");
    } catch (error) {
      console.error("Error saving edit status:", error);
      toast.error("Error saving details. Please try again.");
      throw error;
    }
  };

  const handleJeeEditUpdate = async (allVal: boolean, mainVal: boolean, advVal: boolean) => {
    try {
      const appId = dataToSave?.id;
      if (!appId) {
        toast.error("Application ID not found");
        return;
      }

      let mainStatus = "PENDING";
      let advStatus = "PENDING";

      if (allVal) {
        mainStatus = "JEE_EDITING";
        advStatus = "JEE_EDITING";
      } else {
        if (mainVal) {
          mainStatus = "JEE_EDITING";
        }
        if (advVal) {
          advStatus = "JEE_EDITING";
        }
      }

      const args = `application_ids:${appId},jee_main_verification_status:${mainStatus},jee_advanced_verification_status:${advStatus}`;

      const res = await apiFetch(
        `${apiConfig.getResourceUrl("applications")}?queryId=UPDATE_JEE_VERIFICATION_STATUS_NEW&args=${encodeURIComponent(args)}`,
        { method: "GET" }
      );

      const result = await res.json();
      if (!res.ok || result.errCode === -1) {
        throw new Error(result.message || "Failed to update JEE edit permissions");
      }

      toast.success("JEE Edit permissions updated successfully");

      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === appId
            ? {
                ...app,
                jee_main_verification_status: mainStatus,
                jee_advanced_verification_status: advStatus,
              }
            : app
        )
      );

      setDataToSave((prev: any) => ({
        ...prev,
        jee_main_verification_status: mainStatus,
        jee_advanced_verification_status: advStatus,
      }));

      fetchApplications();
    } catch (error: any) {
      console.error("Error saving edit status:", error);
      toast.error(error.message || "Error saving details. Please try again.");
      throw error;
    }
  };

  const handleMsgSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (quillMsg === "") {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);

    try {
      const userId = dataToSave?.user_id;
      const driveId = dataToSave?.drive_id;

      if (!userId) {
        toast.error("User not identified. Please log in again.");
        return;
      }

      if (!driveId) {
        toast.error("Drive ID not found.");
        return;
      }

      // Step 1: Create Query
      const queryObj: Record<string, string> = {
        subject: "Application Update Required",
        category: "Other General Queries",
        status: "Open",
        user_id: userId,
        drive_id: driveId,
      };

      const jsonString = JSON.stringify(queryObj);
      const base64Encoded = btoa(unescape(encodeURIComponent(jsonString)));
      const queryParams = new FormData();
      queryParams.append("resource", base64Encoded);

      const queryResponse = await queryService.create({ params: queryParams });

      if (queryResponse.errCode === -1) {
        return;
      }

      const queryId = queryResponse.resource[0]?.id;



      // Step 2: Create First Message
      const messageObj: Record<string, string> = {
        sendertype: "Admin",
        message: quillMsg,
        query_Id: queryId,
        user_id: userId,
      };

      const messageParams = new FormData();
      let selectedFile = null;

      selectedFile = Object.keys(dataToSave).filter((key) => dataToSave[key] instanceof File);

      if (selectedFile.length > 0) {
        messageParams.append("photo", dataToSave[selectedFile[0]]);
        dataToSave[selectedFile[0]] = "";

        messageParams.append("description", "message attachment");
        messageParams.append("appId", "admission-iiitb");
        messageParams.append("dmsRole", "admin");
        messageParams.append("user_id", userId);
        messageParams.append("tags", "t1,t2,attend");
      }

      const msgJsonString = JSON.stringify(messageObj);
      const msgBase64Encoded = btoa(unescape(encodeURIComponent(msgJsonString)));
      messageParams.append("resource", msgBase64Encoded);

      const messageResponse = await messageService.create({ params: messageParams });

      if (messageResponse.errCode === -1) {
        toast.error("Unable to send message");
        return;

      }

      // Step 3: Send Notification
      await handleSendNotification(queryId);

      toast.success("Query submitted successfully!");

      // Reset form
      setQuillMsg(""); // Clear message content
      setShowAdv(false);

    } catch (error) {
      console.error("Error submitting query:", error);
      toast.error("Failed to submit query. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (query_id: string) => {
    try {
      const data = await queryService.getByFilter("GET_NOTIFY_USER", `query_id%3A${query_id}`);
      toast.success("Notified applicant");

    } catch (error) {
      console.error("Error notifying user:", error);
      toast.error("Unable to notify applicant");
    }
  };

  const fetchQueryDetails = async (id: string) => {
    setLoading(true);
    try {
      const data = await queryService.getByFilter(
        "GET_EDIT_QUERY_FOR_ADMIN",
        `user_id%3A${id}`
      );
      setQuery(data)
    } catch (error) {
      setQuery(null)
      console.error("Error fetching query:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const cleaned = dateStr.replace(/\[.*\]$/, "");
      const date = new Date(cleaned);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  const handleStatusChange = async (query: Query, newStatus: string) => {
    try {
      const updateObj: Record<string, string> = {
        id: query.id,
        subject: query.subject,
        category: query.category,
        status: newStatus,
        user_id: query.user_id,
        drive_id: query.drive_id || "",
      };
      const jsonString = JSON.stringify(updateObj);
      const base64Encoded = btoa(jsonString);

      await queryService.update(query.id, base64Encoded);
      // fetchMyQueries();
    } catch (error) {
      console.error("Error updating query status:", error);
      toast.error("Failed to update status.");
    }
  };

  const handleAppVerfication = async (checked: boolean) => {
    try {
      const adminEmail = user?.email_id || "admin";
      const now = new Date().toISOString();
      // Always log who changed and when — both for verify and unverify
      const finalPayload = {
        verification_status: checked,
        verification_modified_by: adminEmail,
        verification_modified_at: now,
      };
      await save(finalPayload);

      toast[checked ? "success" : "error"](
        checked ? "Application Verified" : "Application marked as unverified"
      );

      // Optimized: patch just this record instead of re-fetching all 8000+
      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === dataToSave.id
            ? { ...app, verification_status: checked, verification_modified_by: adminEmail, verification_modified_at: now }
            : app
        )
      );
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error verifying application");
      throw error;
    }
  };


  const handleAllowEditButton = async (checked: boolean) => {
    setShowAdv(checked);
    await handleAllowEdit(checked);
  }

  const handleJeeEditButton = async (all: boolean, main: boolean, adv: boolean) => {
    setShowJeeEdit(all);
    setShowJeeMainEdit(main);
    setShowJeeAdvEdit(adv);
    await handleJeeEditUpdate(all, main, adv);
  };

  const handleAppVerfied = (checked: boolean) => {
    setAppVerified(checked);
    handleAppVerfication(checked)
  }

  const handleJeeAdvVerificationChange = async (value: string) => {
    try {
      const appId = dataToSave?.id;
      if (!appId) {
        toast.error("Application ID not found");
        return;
      }

      const res = await apiFetch(
        `${apiConfig.getResourceUrl("applications")}?queryId=UPDATE_JEE_ADV_VERIFICATION&args=${encodeURIComponent(
          `application_ids:${appId},jee_adv_verification_status:${value}`
        )}`,
        { method: "GET" }
      );

      const result = await res.json();
      if (!res.ok || result.errCode === -1) {
        throw new Error(result.message || "Failed to update JEE Advanced verification status");
      }

      toast.success("JEE Advanced Verification status updated successfully");

      const newStatusVal = value === "true" ? true : value === "false" ? false : null;
      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === appId
            ? { ...app, jee_adv_verification_status: newStatusVal }
            : app
        )
      );

      setDataToSave((prev: any) => ({
        ...prev,
        jee_adv_verification_status: newStatusVal,
      }));

      // Refresh data to fetch recalculated BJR/IIR ranks
      fetchApplications();

    } catch (error: any) {
      console.error("Error updating JEE Advanced verification:", error);
      toast.error(error.message || "Error updating JEE Advanced verification status");
    }
  };

  const handleJeeMainVerificationChange = async (value: string) => {
    try {
      const appId = dataToSave?.id;
      if (!appId) {
        toast.error("Application ID not found");
        return;
      }

      const res = await apiFetch(
        `${apiConfig.getResourceUrl("applications")}?queryId=UPDATE_JEE_VERIFICATION_STATUS_NEW&args=${encodeURIComponent(
          `application_ids:${appId},jee_main_verification_status:${value}`
        )}`,
        { method: "GET" }
      );

      const result = await res.json();
      if (!res.ok || result.errCode === -1) {
        throw new Error(result.message || "Failed to update JEE Main verification status");
      }

      toast.success("JEE Main Verification status updated successfully");

      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === appId
            ? { ...app, jee_main_verification_status: value }
            : app
        )
      );

      setDataToSave((prev: any) => ({
        ...prev,
        jee_main_verification_status: value,
      }));

      // Refresh data to fetch recalculated BJR/IIR ranks
      fetchApplications();

    } catch (error: any) {
      console.error("Error updating JEE Main verification:", error);
      toast.error(error.message || "Error updating JEE Main verification status");
    }
  };

  const handleJeeAdvancedVerificationChange = async (value: string) => {
    try {
      const appId = dataToSave?.id;
      if (!appId) {
        toast.error("Application ID not found");
        return;
      }

      const res = await apiFetch(
        `${apiConfig.getResourceUrl("applications")}?queryId=UPDATE_JEE_VERIFICATION_STATUS_NEW&args=${encodeURIComponent(
          `application_ids:${appId},jee_advanced_verification_status:${value}`
        )}`,
        { method: "GET" }
      );

      const result = await res.json();
      if (!res.ok || result.errCode === -1) {
        throw new Error(result.message || "Failed to update JEE Advanced verification status");
      }

      toast.success("JEE Advanced Verification status updated successfully");

      setAllApplications((prev) =>
        prev.map((app) =>
          app.id === appId
            ? { ...app, jee_advanced_verification_status: value }
            : app
        )
      );

      setDataToSave((prev: any) => ({
        ...prev,
        jee_advanced_verification_status: value,
      }));

      // Refresh data to fetch recalculated BJR/IIR ranks
      fetchApplications();

    } catch (error: any) {
      console.error("Error updating JEE Advanced verification:", error);
      toast.error(error.message || "Error updating JEE Advanced verification status");
    }
  };

  // console.log("allApplications", allApplications[0]?.verification_status !== filterVerificationStatus)
  // console.log("filteredApplications", filteredApplications)
  // console.log("filterVerificationStatus", filterVerificationStatus)
  return (
    <div className="dash-layout">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p) => !p)}
      />

      <div
        className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""}`}
      >
        <header className="dash-header">
          <h1 className="dash-page-title">Applications</h1>
          <div className="dash-profile-sec">
            {/* <img src={HelpIcon} className="help_icon" alt="help-icon" /> */}
            <div className="dash-user-profile">
              <div
                className="dash-profile-circle"
                onClick={() => setShowDropdown((p) => !p)}
              >
                <span>A</span>
              </div>
              {showDropdown && (
                <div className="dash-profile-dropdown">
                  <LogoutButton />
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dash-content-body">
          <div className="app-table-container">
            <div className="app-table-header">
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <h3>All Applications</h3>
                <span className="app-count">{totalCount}</span>
              </div>
            </div>

            {/* Filter Bar */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                padding: "12px 16px",
                borderBottom: "1px solid #e5e7eb",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >


              <select
                value={filterDriveId}
                onChange={(e) => setFilterDriveId(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "160px",
                }}
              >
                <option value="">Drive</option>
                {drives.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>

              <select
                value={filterPref}
                onChange={(e) => {
                  setFilterPref(e.target.value);
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "160px",
                }}
              >
                <option value="">Programmes</option>
                <option value="MTech_CSE">MTech CSE</option>
                <option value="MTech_ECE">MTech ECE</option>
                <option value="MTech_AI&DS">MTech AI&DS</option>
              </select>

              <select
                value={filterFormStatus}
                onChange={(e) => setFilterFormStatus(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "140px",
                }}
              >
                <option value="">Application Status</option>
                <option value="DRAFT">Draft</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="EDITING">Editing</option>
                <option value="EDIT_SUBMIT">Edit Submit</option>
              </select>

              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "150px",
                }}
              >
                <option value="">Payment Status</option>
                <option value="SUCCESS">Success</option>
                <option value="PENDING">Created</option>
                <option value="FAILED">Failed</option>
                <option value="NOT_PAID">Not Paid</option>
              </select>

              <select
                value={filterVerificationStatus}
                onChange={(e) => setFilterVerificaitonStatus(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "150px",
                }}
              >
                <option value="">Verification Status</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>

              <select
                value={filterJeeMainVerificationStatus}
                onChange={(e) => setFilterJeeMainVerificationStatus(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "185px",
                }}
              >
                <option value="">JEE Main Verification</option>
                <option value="PENDING">Pending</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="NOT_ACCEPTED">Not Accepted</option>
                <option value="JEE_EDITING">JEE Editing</option>
                <option value="JEE_MAIN_RESUBMITTED">JEE Main Resubmitted</option>
              </select>

              <select
                value={filterJeeAdvVerificationStatus}
                onChange={(e) => setFilterJeeAdvVerificationStatus(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "185px",
                }}
              >
                <option value="">JEE Advanced Verification</option>
                <option value="PENDING">Pending</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="NOT_ACCEPTED">Not Accepted</option>
                <option value="JEE_EDITING">JEE Editing</option>
                <option value="JEE_ADVANCED_RESUBMITTED">JEE Advanced Resubmitted</option>
              </select>



              <button
                onClick={exportToCSV}
                disabled={exporting || loading}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: "1px solid #10b981",
                  backgroundColor: "#f0fdf4",
                  color: "#059669",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: exporting || loading ? "not-allowed" : "pointer",
                  opacity: exporting || loading ? 0.6 : 1,
                }}
              >
                {exporting ? "Exporting..." : "Export"}
              </button>

              {(filterFormStatus ||
                filterPaymentStatus ||
                filterVerificationStatus ||
                filterJeeMainVerificationStatus ||
                filterJeeAdvVerificationStatus ||
                filterDriveId ||
                filterPref ||
                searchText) && (
                  <button
                    onClick={() => {
                      setFilterFormStatus("");
                      setFilterPaymentStatus("");
                      setFilterVerificaitonStatus("");
                      setFilterJeeMainVerificationStatus("");
                      setFilterJeeAdvVerificationStatus("");
                      setFilterDriveId("");
                      setFilterPref("");
                      setPageNo(1);
                    }}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      fontSize: "13px",
                      color: "#dc2626",
                      backgroundColor: "#fef2f2",
                      cursor: "pointer",
                    }}
                  >
                    Clear Filters
                  </button>
                )}

              <input
                type="text"
                placeholder="Search by App No, Name or Email..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "13px",
                  color: "#374151",
                  backgroundColor: "#fff",
                  minWidth: "220px",
                }}
              />
            </div>

            {loading ? (
              <div className="app-loading">Loading applications...</div>
            ) : applications.length === 0 ? (
              <div className="app-empty">No applications found.</div>
            ) : (
              <div className="app-table-wrapper">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            applications.filter((app) => app.status !== "DRAFT")
                              .length > 0 &&
                            selectedIds.length ===
                            applications.filter(
                              (app) => app.status !== "DRAFT",
                            ).length
                          }
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>#</th>
                      <th>Application No.</th>
                      <th>User Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Drive</th>
                      <th>Gate Score/ BJR</th>
                      <th>JEE Main Rank</th>
                      <th>JEE Advanced Rank</th>
                      <th>JEE Main Score</th>
                      <th>JEE Advanced Score</th>
                      <th style={{ textAlign: "center" }}>JEE Main Verification Status</th>
                      <th style={{ textAlign: "center" }}>JEE Advanced Verification Status</th>
                      <th>Application Status</th>
                      <th>Payment Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app, idx) => {
                      const extra = app.extra_data || {};

                      let scoreDisplay = "—";
                      const currentDrive = drives.find((d) => d.id === app.drive_id);
                      const driveKind = currentDrive?.type?.toLowerCase();
                      const currentKind = driveKind || globalKind;
                      const isBtech = currentKind === "btech" || app.application_number?.startsWith("APBTIMT");

                      if (isBtech) {
                        scoreDisplay = app.jeemain_rank ? app.jeemain_rank.toString() : "—";
                      } else {
                        const PROG_SHORT_NAMES: Record<string, string> = {
                          "MTech_CSE": "CSE",
                          "MTech_ECE": "ECE",
                          "MTech_AI_DS": "AI&DS",
                          "MTech_AI&DS": "AI&DS",
                        };
                        const getProgName = (prefId: any) => PROG_SHORT_NAMES[prefId] || prefId || "Unknown";

                        const hasMultiplePrefs = (app.pref1 && app.pref1 !== "—" && app.pref2 && app.pref2 !== "—");

                        if (hasMultiplePrefs) {
                          const prefs = [];
                          if (app.pref1 && app.pref1 !== "—") prefs.push(`${getProgName(app.pref1)}: ${app.pref1score || "NA"}`);
                          if (app.pref2 && app.pref2 !== "—") prefs.push(`${getProgName(app.pref2)}: ${app.pref2score || "NA"}`);
                          if (app.pref3 && app.pref3 !== "—") prefs.push(`${getProgName(app.pref3)}: ${app.pref3score || "NA"}`);
                          scoreDisplay = prefs.join(", ");
                        } else if (app.pref1score) {
                          scoreDisplay = app.pref1score.toString();
                        }
                      }

                      return (
                        <tr key={app.id}>
                          <td>
                            <input
                              type="checkbox"
                              disabled={app.status === "DRAFT"}
                              checked={selectedIds.includes(app.id)}
                              onChange={() => handleRowSelect(app.id)}
                            />
                          </td>
                          <td>{(pageNo - 1) * pageSize + idx + 1}</td>
                          <td>
                            <button
                              className="app-number-link"
                              onClick={() =>
                                fetchApplicationDetails(
                                  app.user_id,
                                  app.application_number,
                                )
                              }
                              title="View full details"
                            >
                              {app.application_number}
                            </button>
                          </td>
                          <td>{extra.user_name || "—"}</td>
                          <td>{extra.user_email || "—"}</td>
                          <td>{extra.user_mobile || "—"}</td>
                          <td>{extra.drive_name || "—"}</td>
                          <td>{scoreDisplay}</td>
                          <td>{app?.["jeemain_rank"] || "—"}</td>
                          <td>{app?.["jeeadv_rank"] || "—"}</td>
                          <td>{app?.["jeemain_score"] || "—"}</td>
                          <td>{app?.["jeeadv_score"] || "—"}</td>
                          <td style={{ textAlign: "center" }}>
                            {renderCircleStatus(app.jee_main_verification_status)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {renderCircleStatus(app.jee_advanced_verification_status)}
                          </td>
                          <td>
                            <span
                              className="app-status-badge"
                              style={{
                                backgroundColor: `${STATUS_COLORS[app.status] || "#6b7280"}20`,
                                color: STATUS_COLORS[app.status] || "#6b7280",
                              }}
                            >
                              {app.status?.replace(/_/g, " ") || "—"}
                            </span>
                          </td>
                          <td>
                            <span
                              className="app-status-badge"
                              style={{
                                backgroundColor: `${PAYMENT_COLORS[extra.payment_status || "NOT_PAID"] || "#6b7280"}20`,
                                color:
                                  PAYMENT_COLORS[
                                  extra.payment_status || "NOT_PAID"
                                  ] || "#6b7280",
                              }}
                            >
                              {(extra.payment_status || "NOT PAID")?.replace(
                                /_/g,
                                " ",
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading && totalCount > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    Rows per page:
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      fontSize: "13px",
                      color: "#374151",
                      backgroundColor: "#fff",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginLeft: "8px",
                    }}
                  >
                    Showing {Math.min((pageNo - 1) * pageSize + 1, totalCount)}–
                    {Math.min(pageNo * pageSize, totalCount)} of {totalCount}
                  </span>
                </div>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={() => setPageNo((p) => Math.max(1, p - 1))}
                    disabled={pageNo <= 1}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      fontSize: "13px",
                      color: pageNo <= 1 ? "#9ca3af" : "#374151",
                      backgroundColor: "#fff",
                      cursor: pageNo <= 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Previous
                  </button>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    Page {pageNo} of{" "}
                    {Math.max(1, Math.ceil(totalCount / pageSize))}
                  </span>
                  <button
                    onClick={() => setPageNo((p) => p + 1)}
                    disabled={pageNo >= Math.ceil(totalCount / pageSize)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      fontSize: "13px",
                      color:
                        pageNo >= Math.ceil(totalCount / pageSize)
                          ? "#9ca3af"
                          : "#374151",
                      backgroundColor: "#fff",
                      cursor:
                        pageNo >= Math.ceil(totalCount / pageSize)
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Popup Modal */}
      {showPopup && (
        <div className="app-popup-overlay" onClick={() => setShowPopup(false)}>
          <div
            className="app-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-popup-header">
              <h2>Application Details</h2>
              <span className="app-popup-appno">{popupAppNumber}</span>
              <button
                className="app-popup-close"
                onClick={() => setShowPopup(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="app-popup-body">
              {(() => {
                const driveId = selectedApplication?.[0]?.drive_id;
                const drive = drives.find((d) => d.id === driveId);
                const driveKind = drive?.type?.toLowerCase();
                const currentKind = driveKind || globalKind;
                const isBtech = currentKind === "btech" ||
                  popupAppNumber?.startsWith("APBTIMT") ||
                  selectedApplication?.[0]?.application_number?.startsWith("APBTIMT");

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "16px", padding: "0 10px" }}>
                    <div>
                      <label className="form-label">Application Status:{" "}</label>
                      <label className="form-label" style={{ fontWeight: "normal" }}>{selectedApplication?.[0]?.status === "EDITING" || (showAdv && !isJeeMainEditing && !isJeeAdvEditing) ? "Editing" :
                        selectedApplication?.[0]?.status === "DRAFT" ? "Draft" :
                          selectedApplication?.[0]?.status === "SUBMITTED" ? "Submitted" :
                            selectedApplication?.[0]?.status === "EDITING" ? "Editing" :
                              selectedApplication?.[0]?.status === "EDIT_SUBMIT" ? "Edit Submit" :
                                selectedApplication?.[0]?.status === "JEE_EDITING" ? "JEE Editing" :
                                  selectedApplication?.[0]?.status === "JEE_MAIN_EDITING" ? "JEE Main Editing" :
                                    selectedApplication?.[0]?.status === "JEE_ADV_EDITING" ? "JEE Advanced Editing" :
                                      selectedApplication?.[0]?.status === "JEE_EDIT_SUBMIT" ? "JEE Submit" :
                                        selectedApplication?.[0]?.status === "JEE_MAIN_EDIT_SUBMIT" ? "JEE Main Submit" :
                                          selectedApplication?.[0]?.status === "JEE_ADV_EDIT_SUBMIT" ? "JEE Advanced Submit" :
                                            "NA"}{" "}</label>
                    </div>
                    <div>
                      <label className="form-label">Payment Status:{" "}</label>
                      <label className="form-label" style={{ fontWeight: "normal" }}>{
                        selectedApplication?.[0]?.payment_status === "SUCCESS" ? "Success" :
                          selectedApplication?.[0]?.payment_status === "PENDING" ? "Created" :
                            selectedApplication?.[0]?.payment_status === "FAILED" ? "Failed" :
                              selectedApplication?.[0]?.payment_status === "NOT_PAID" ? "Not Paid   " :
                                "NA"}{" "}</label>
                    </div>
                    <div>
                      <label className="form-label">Application Verification Status:{" "}</label>
                      <label className="form-label" style={{ fontWeight: "normal" }}>{appVerified ? "True" : "False"}{" "}</label>
                    </div>
                    {isBtech && (
                      <>
                        <div>
                          <label className="form-label">JEE Main Verification Status:{" "}</label>
                          <label className="form-label" style={{ fontWeight: "normal" }}>{formatEnumStatus(selectedApplication?.[0]?.jee_main_verification_status)}{" "}</label>
                        </div>
                        <div>
                          <label className="form-label">JEE Advanced Verification Status:{" "}</label>
                          <label className="form-label" style={{ fontWeight: "normal" }}>{formatEnumStatus(selectedApplication?.[0]?.jee_advanced_verification_status)}{" "}</label>
                        </div>
                      </>
                    )}
                    {!isBtech && (
                      <div>
                        <label className="form-label">JEE Advanced Verification Status:{" "}</label>
                        <label className="form-label" style={{ fontWeight: "normal" }}>{
                          selectedApplication?.[0]?.jee_adv_verification_status === true ? "Accepted" :
                            selectedApplication?.[0]?.jee_adv_verification_status === false ? "Not Accepted" :
                              "Pending"
                        }{" "}</label>
                      </div>
                    )}

                    {selectedApplication?.[0]?.verification_modified_by && (
                      <div>
                        <label className="form-label">Last Changed By:{" "}</label>
                        <label className="form-label" style={{ fontWeight: "normal" }}>{selectedApplication[0].verification_modified_by}{" "}</label>
                      </div>
                    )}
                    {selectedApplication?.[0]?.verification_modified_at && (
                      <div>
                        <label className="form-label">Last Changed At:{" "}</label>
                        <label className="form-label" style={{ fontWeight: "normal" }}>{formatDate(selectedApplication[0].verification_modified_at)}{" "}</label>
                      </div>
                    )}
                    <div>
                      {selectedApplication?.[0]?.last_updated_jee_main && (
                        <div>
                          <label className="form-label">Last Updated By Candidate (JEE Main):{" "}</label>
                          <label className="form-label" style={{ fontWeight: "normal" }}>{formatDate(selectedApplication?.[0]?.last_updated_jee_main)}{" "}</label>
                        </div>
                      )}
                      {selectedApplication?.[0]?.last_updated_jee_adv && (
                        <div>
                          <label className="form-label">Last Updated By Candidate (JEE Advanced):{" "}</label>
                          <label className="form-label" style={{ fontWeight: "normal" }}>{formatDate(selectedApplication?.[0]?.last_updated_jee_adv)}{" "}</label>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {popupData?.admin_rejection_reason && (
                <div style={{ margin: "0 24px 20px", padding: "16px", backgroundColor: "#fef2f2", border: "1px solid #fee2e2", borderRadius: "8px" }}>
                  <h4 style={{ margin: "0 0 8px 0", color: "#991b1b", fontSize: "14px", fontWeight: 700 }}>ADMINISTRATIVE REJECTION</h4>
                  <p style={{ margin: 0, fontSize: "14px", color: "#b91c1c" }}>
                    <strong>Reason:</strong> {popupData.admin_rejection_reason}
                  </p>
                  {popupData.admin_rejected_programmes && (
                    <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#b91c1c" }}>
                      <strong>Rejected for:</strong> {popupData.admin_rejected_programmes.replace(/,/g, ", ").replace(/_/g, " ")}
                    </p>
                  )}
                </div>
              )}
              {popupLoading ? (
                <div className="app-loading">Loading details...</div>
              ) : !popupData || Object.keys(popupData).length === 0 ? (
                <div className="app-empty">
                  No details found for this application.
                </div>
              ) : (
                Object.entries(SECTION_LABELS).map(
                  ([sectionKey, sectionLabel]) => {
                    const sectionData = popupData[sectionKey];
                    if (!sectionData || sectionKey === "gate_details") return null;

                    const driveId = (popupData as any)?.drive_id;
                    const drive = drives.find((d) => d.id === driveId);
                    const driveKind = drive?.type?.toLowerCase() as AdmissionDriveKind | undefined;

                    // Priority 1: Record's specific drive kind
                    // Priority 2: Global kind from hook (as fallback or override as per user pattern)
                    // Priority 3: Application number prefix
                    const currentKind = driveKind || globalKind;
                    const isBtech = currentKind === "btech" ||
                      popupAppNumber?.startsWith("APBTIMT") ||
                      (popupData as any).application_number?.startsWith("APBTIMT");

                    const isExpanded = expandedSections.has(sectionKey);

                    const orderedKeys = SECTION_FIELD_ORDER[sectionKey] || [];
                    const allKeys = Object.keys(sectionData).filter(
                      (k) => !SKIP_FIELDS.has(k),
                    );
                    let sortedKeys = [
                      ...orderedKeys.filter((k) => allKeys.includes(k) || k.startsWith("_")),
                      ...allKeys.filter((k) => !orderedKeys.includes(k)),
                    ];

                    // BTech-specific pruning and normalization
                    if (isBtech) {
                      if (sectionKey === "programme_preference") {
                        // Only show preferences 1-5, skip everything else (rollno, score, year, subjects, scorecard)
                        sortedKeys = sortedKeys.filter((k) =>
                          k === "pref1" || k === "pref2" || k === "pref3" || k === "pref4" || k === "pref5"
                        );
                      } else if (sectionKey === "education_details") {
                        const eduData = sectionData;
                        const isPercentage10 = String(eduData.edu10_marksformat).toLowerCase().includes("percentage");
                        const isPercentage12 = String(eduData.edu12_marksformat).toLowerCase().includes("percentage");

                        sortedKeys = sortedKeys.filter((k) => {
                          // If percentage, hide marks obtained, maximum marks, and grade for 10th
                          if (isPercentage10 && ["edu10_marksobtained", "edu10_maximummarks", "edu10_grade"].includes(k)) return false;
                          // If percentage, hide marks obtained, maximum marks, and grade for 12th
                          if (isPercentage12 && ["edu12_marksobtained", "edu12_maximummarks", "edu12_grade"].includes(k)) return false;
                          // Also hide edu12_since for BTech as it's not in the form
                          if (k === "edu12_since") return false;
                          return true;
                        });
                      }
                    } else if (currentKind === "mtech") {
                      // MTech-specific pruning: MTech only allows 3 preferences
                      if (sectionKey === "programme_preference") {
                        sortedKeys = sortedKeys.filter((k) =>
                          !["pref4", "pref5"].includes(k)
                        );
                      }
                    }

                    const fields = sortedKeys.map(
                      (k) => [k, sectionData[k]] as [string, any],
                    );
                    return (
                      <div key={sectionKey} className="app-popup-section">
                        <button
                          className="app-popup-section-header"
                          onClick={() => toggleSection(sectionKey)}
                        >
                          <span>{sectionLabel}</span>
                          {isExpanded ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="app-popup-section-body">
                            {fields.length === 0 ? (
                              <p className="app-no-data">No data provided.</p>
                            ) : (
                              <div className="app-detail-grid">
                                {fields.map(([key, val]) => {
                                  const isHeader = key.startsWith("_");
                                  if (isHeader) {
                                    const showMainDropdown = isBtech && key === "_jeemain_header";
                                    const showAdvDropdown = isBtech && key === "_jeeadv_header";
                                    return (
                                      <div key={key} className="app-detail-subheader" style={{ gridColumn: "1 / -1", marginTop: "1rem", marginBottom: "0.5rem", borderBottom: "1px solid #eee", paddingBottom: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <h4 style={{ margin: 0, fontSize: "14px", color: "#374151", fontWeight: 700 }}>
                                          {formatFieldLabel(key)}
                                        </h4>
                                        {showMainDropdown && (
                                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <label className="form-label" style={{ fontSize: "13px", fontWeight: "600", color: "#374151", margin: 0 }}>Verify JEE Main: </label>
                                            <select
                                              value={selectedApplication?.[0]?.jee_main_verification_status || "PENDING"}
                                              onChange={(e) => handleJeeMainVerificationChange(e.target.value)}
                                              style={{
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #d1d5db",
                                                fontSize: "13px",
                                                color: "#374151",
                                                backgroundColor: "#fff",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <option value="PENDING">Pending</option>
                                              <option value="ACCEPTED">Accepted</option>
                                              <option value="NOT_ACCEPTED">Not Accepted</option>
                                              <option value="JEE_EDITING">JEE Editing</option>
                                              <option value="JEE_MAIN_RESUBMITTED">JEE Main Resubmitted</option>
                                            </select>
                                          </div>
                                        )}
                                        {showAdvDropdown && (
                                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <label className="form-label" style={{ fontSize: "13px", fontWeight: "600", color: "#374151", margin: 0 }}>Verify JEE Advanced: </label>
                                            <select
                                              value={selectedApplication?.[0]?.jee_advanced_verification_status || "PENDING"}
                                              onChange={(e) => handleJeeAdvancedVerificationChange(e.target.value)}
                                              style={{
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #d1d5db",
                                                fontSize: "13px",
                                                color: "#374151",
                                                backgroundColor: "#fff",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <option value="PENDING">Pending</option>
                                              <option value="ACCEPTED">Accepted</option>
                                              <option value="NOT_ACCEPTED">Not Accepted</option>
                                              <option value="JEE_EDITING">JEE Editing</option>
                                              <option value="JEE_ADVANCED_RESUBMITTED">JEE Advanced Resubmitted</option>
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={key} className="app-detail-item">
                                      <span className="app-detail-label">
                                        {isBtech && sectionKey === "programme_preference" && key.match(/^pref[1-5]$/)
                                          ? `Preference ${key.charAt(4)}`
                                          : formatFieldLabel(key)}
                                      </span>
                                      <span className="app-detail-value">
                                        {formatFieldValue(key, val)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  },
                )
              )}

              {/* Download PDF & Send Email Buttons */}
              {!popupLoading &&
                popupData &&
                Object.keys(popupData).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                      padding: "20px 0 8px",
                    }}
                  >
                    <button
                      id="download-pdf-btn"
                      onClick={async () => {
                        try {
                          const btn = document.getElementById(
                            "download-pdf-btn",
                          ) as HTMLButtonElement;
                          if (btn) {
                            btn.disabled = true;
                            btn.textContent = "Generating PDF...";
                          }

                          const response = await apiFetch(
                            `${apiConfig.API_BASE_URL}/payments/download-pdf/${popupAppNumber}`,
                            { method: "GET" },
                          );

                          if (!response.ok) {
                            throw new Error("Failed to download PDF");
                          }

                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `Application_${popupAppNumber}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);

                          if (btn) {
                            btn.disabled = false;
                            btn.textContent = "Download Application PDF";
                          }
                        } catch (err) {
                          console.error("PDF download failed:", err);
                          toast.error("Failed to download PDF");
                          const btn = document.getElementById(
                            "download-pdf-btn",
                          ) as HTMLButtonElement;
                          if (btn) {
                            btn.disabled = false;
                            btn.textContent = "Download Application PDF";
                          }
                        }
                      }}
                      style={{
                        padding: "10px 24px",
                        backgroundColor: "#003366",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#004d99")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "#003366")
                      }
                    >
                      Download Application PDF
                    </button>

                    <button
                      id="send-email-btn"
                      onClick={async () => {
                        try {
                          const btn = document.getElementById(
                            "send-email-btn",
                          ) as HTMLButtonElement;
                          if (btn) {
                            btn.disabled = true;
                            btn.textContent = "Sending Email...";
                          }

                          const response = await apiFetch(
                            `${apiConfig.API_BASE_URL}/admin/email/send-submission-email/${popupAppNumber}`,
                            { method: "POST" },
                          );

                          const result = await response.json();

                          if (!response.ok || result.status !== "SUCCESS") {
                            throw new Error(
                              result.message || "Failed to send email",
                            );
                          }

                          toast.success(result.message || "Email sent successfully");

                          if (btn) {
                            btn.disabled = false;
                            btn.textContent = "Send Submission Email";
                          }
                        } catch (err: any) {
                          console.error("Send email failed:", err);
                          toast.error(err.message || "Failed to send email");
                          const btn = document.getElementById(
                            "send-email-btn",
                          ) as HTMLButtonElement;
                          if (btn) {
                            btn.disabled = false;
                            btn.textContent = "Send Submission Email";
                          }
                        }
                      }}
                      style={{
                        padding: "10px 24px",
                        backgroundColor: "#059669",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#047857")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "#059669")
                      }
                    >
                      Send Submission Email
                    </button>
                    <button
                      onClick={() => {
                        setRejectionReason("");
                        setRejectionProgramme("");
                        setShowRejectionModal(true);
                      }}
                      style={{
                        padding: "10px 24px",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#b91c1c")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "#dc2626")
                      }
                    >
                      Reject Application
                    </button>
                  </div>
                )}

              {(dataToSave?.status === "EDITING" ||
                dataToSave?.status === "SUBMITTED" ||
                dataToSave?.status === "EDIT_SUBMIT" ||
                dataToSave?.status === "JEE_EDITING" ||
                dataToSave?.status === "JEE_MAIN_EDITING" ||
                dataToSave?.status === "JEE_ADV_EDITING" ||
                dataToSave?.status === "JEE_EDIT_SUBMIT" ||
                dataToSave?.status === "JEE_MAIN_EDIT_SUBMIT" ||
                dataToSave?.status === "JEE_ADV_EDIT_SUBMIT"
              ) &&
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px", margin: "10px 0" }}>
                  <div>
                    <label className="form-label" style={{ marginLeft: 10 }}>Allow Edit Application {" "}</label>
                    <input
                      type="checkbox"
                      checked={isApplicationEditing}
                      style={{ marginLeft: 10 }}
                      onChange={(e) => handleAllowEditButton(e.target.checked)}
                    />
                  </div>
                  <div>
                    <label className="form-label" style={{ marginLeft: 10 }}>Application Verified{" "}</label>
                    <input
                      type="checkbox"
                      checked={appVerified}
                      style={{ marginLeft: 10 }}
                      onChange={(e) => handleAppVerfied(e.target.checked)}
                    />
                  </div>
                </div>}
              {showAdv &&
                <>
                  <div className="help-field">
                    <label>Query's Description / Message*</label>
                    <ReactQuill
                      theme="snow"
                      id="description"
                      value={quillMsg}
                      placeholder="Ask your query"
                      onChange={setQuillMsg} />
                  </div>
                  <div className="help-submit">
                    <button type="submit" onClick={handleSubmit} disabled={loading}>
                      {loading ? "Submitting..." : "Send"}
                    </button>
                  </div>
                </>
              }
              <div key={popupAppNumber}>
                {query && query?.length >= 0 &&
                  query?.map((q: any, i: number) => (
                    <div
                      className="query-row"
                      key={q.id || i}
                    >
                      <div className="query-box"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_API_BASE_URL}/query/${q?.id}`, "_blank", "noopener,noreferrer");
                        }
                          // navigate(`/query/${q?.id}`)
                        }
                      >

                        <div className="query-left">
                          <h4>{q.subject || "Untitled Query"}</h4>
                        </div>
                        <div className="query-right">
                          <span
                            className={
                              q.status === "Open"
                                ? "status open" :
                                q.status === "Resolved" ?
                                  "status resolved" :
                                  q.status === "In Progress" ?
                                    "status update" : "status closed"
                            }
                          >
                            {q.status}
                          </span>

                          <div className="submitted-date">
                            Submitted : {formatDate(q.last_updated_at)}
                          </div>
                        </div>
                      </div>

                      <div className="status-update">
                        <span
                          style={{ cursor: q.status === "Closed" ? "default" : "pointer" }}
                          className={q.status === "Closed" ? "update query closed" : "update query"}
                          onClick={() => q.status !== "Closed" ? handleStatusChange(q, "Closed") : null}
                        >
                          {q.status !== "Closed" ? "Mark Closed" : "Closed"}
                        </span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Rejection Modal */}
      {showRejectionModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "500px", height: "auto" }}>
            <div className="app-popup-header">
              <h2>Reject Application</h2>
              <button className="app-popup-close" onClick={closeRejectionModal}>
                <X size={20} />
              </button>
            </div>
            <div className="app-popup-body" style={{ padding: "24px" }}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                  Select Programme to Reject*
                </label>
                <select
                  value={rejectionProgramme}
                  onChange={(e) => setRejectionProgramme(e.target.value)}
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px" }}
                >
                  <option value="" disabled>Select</option>
                  <option value="ALL">Reject Entire Application</option>
                  {getEligibleProgrammes().map((prog) => (
                    <option key={prog} value={prog}>
                      Reject Only: {prog.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                  Rejection Reason*
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a detailed reason for rejection..."
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    resize: "vertical"
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  onClick={closeRejectionModal}
                  style={{ padding: "10px 20px", borderRadius: "8px", border: "1px solid #d1d5db", backgroundColor: "#fff", color: "#374151", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectApplication}
                  disabled={rejectionLoading}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#dc2626",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: rejectionLoading ? "not-allowed" : "pointer",
                    opacity: rejectionLoading ? 0.7 : 1
                  }}
                >
                  {rejectionLoading ? "Rejecting..." : "Confirm Rejection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
